import { ConnectionQuality, ConnectionState } from 'livekit-client'

import type { DeviceAccess } from '../deviceProbe.ts'
import type { StringKey } from '../i18n.ts'
import type { ShareFailure } from '../screenShare.ts'

export type VerdictStatus = 'ok' | 'warn' | 'bad' | 'unknown'
export interface Verdict {
  status: VerdictStatus
  key: StringKey
  action?:
    | 'fixMic'
    | 'fixCam'
    | 'unmute'
    | 'enableCam'
    | 'restartMedia'
    | 'unlockAudio'
    | 'pickSpeaker'
    | 'shareHelp'
    | 'closeBoard'
    | 'pip'
    | 'pipHelp'
}

const problem = (access: DeviceAccess | null | undefined) =>
  access === 'blocked' || access === 'missing' || access === 'busy'

export function micVerdict({
  published,
  enabled,
  access,
}: {
  published: boolean
  enabled: boolean
  access: DeviceAccess | null
  level: number | null
  quietForMs: number
}): Verdict {
  if (!published || !enabled) return { status: 'warn', key: 'help.mic.muted', action: 'unmute' }
  if (problem(access)) return { status: 'bad', key: 'help.mic.blocked', action: 'fixMic' }
  if (access === 'silent') return { status: 'bad', key: 'help.mic.silent', action: 'fixMic' }
  return { status: 'ok', key: 'help.ok' }
}

export function camVerdict({
  published,
  enabled,
  access,
  frozen,
}: {
  published: boolean
  enabled: boolean
  access: DeviceAccess | null
  frozen: boolean
}): Verdict {
  if (!published || !enabled) return { status: 'warn', key: 'help.cam.off', action: 'enableCam' }
  if (problem(access)) return { status: 'bad', key: 'help.cam.blocked', action: 'fixCam' }
  if (frozen) return { status: 'bad', key: 'help.cam.frozen', action: 'restartMedia' }
  return { status: 'ok', key: 'help.ok' }
}

export function speakerVerdict({
  supported,
  blocked,
}: {
  supported: boolean
  blocked: boolean
}): Verdict {
  if (!supported) return { status: 'unknown', key: 'help.speaker.unavailable' }
  if (blocked) return { status: 'bad', key: 'help.speaker.blocked', action: 'unlockAudio' }
  return { status: 'ok', key: 'help.ok' }
}

export function shareVerdict({
  supported,
  sharing,
  boardOpen,
  lastFailure,
}: {
  supported: boolean
  sharing: boolean
  boardOpen: boolean
  lastFailure: ShareFailure | null
}): Verdict {
  if (!supported) return { status: 'unknown', key: 'help.share.unavailable' }
  if (sharing && boardOpen) return { status: 'bad', key: 'help.share.board', action: 'closeBoard' }
  if (sharing) return { status: 'ok', key: 'help.ok' }
  if (lastFailure && lastFailure !== 'cancelled')
    return { status: 'bad', key: 'help.share.failed', action: 'shareHelp' }
  return { status: 'ok', key: 'help.ok' }
}

export function connectionVerdict({
  quality,
  state,
}: {
  quality: ConnectionQuality
  state: ConnectionState
}): Verdict {
  if (state !== ConnectionState.Connected || quality === ConnectionQuality.Lost)
    return { status: 'bad', key: 'help.connection.lost', action: 'restartMedia' }
  if (quality === ConnectionQuality.Poor)
    return { status: 'warn', key: 'help.connection.poor', action: 'restartMedia' }
  return { status: 'ok', key: 'help.ok' }
}
